import { gql } from "@apollo/client";

const listOpenPOs = /* GraphQL */ `
  query POByDefinerID(
    $definerID: ID!
    $openQty: ModelFloatKeyConditionInput
    $filter: ModelPOFilterInput
    $limit: Int
    $nextToken: String
  ) {
    POByDefinerID(
      definerID: $definerID
      openQty: $openQty
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        number
        itemNumber
        qty
        openQty
        CRD
        statusCode
        vendor {
          id
          name
        }
        materialEntityRelation {
          id
          code
        }
      }
      nextToken
      __typename
    }
  }
`;

const getShipmentsByPO = /* GraphQL */ `
  query ReceivingAndShippingByPO(
    $poID: ID!
    $limit: Int
    $nextToken: String
  ) {
    receivingAndShippingByPO(
      poID: $poID
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        qty
        type
      }
      nextToken
      __typename
    }
  }
`;

/**
 * Audits all incoming orders (POs with openQty > 0) and calculates the correct
 * open quantity based on actual shipment/receiving records. Read-only — no updates.
 *
 * @param {Object} params
 * @param {Object} params.client - Apollo Client instance
 * @param {string} params.definerID - The entity/definer ID to scope the query
 * @param {Function} [params.onProgress] - Optional callback(current, total) for progress updates
 * @returns {Promise<Array>} Array of audit result objects
 */
export default async function auditIncomingOrderOpenQuantities({
  client,
  definerID,
  onProgress,
}) {
  // Step 1: Fetch all POs with openQty > 0
  let nextToken = null;
  const allPOs = [];

  do {
    const { data } = await client.query({
      query: gql(listOpenPOs),
      variables: {
        definerID,
        openQty: { gt: 0 },
        limit: 1000,
        nextToken,
      },
      fetchPolicy: "network-only",
    });

    const items = data?.POByDefinerID?.items || [];
    allPOs.push(...items);
    nextToken = data?.POByDefinerID?.nextToken;
  } while (nextToken);

  // Step 2: For each PO, fetch all shipments and calculate the correct openQty
  const results = [];

  for (let i = 0; i < allPOs.length; i++) {
    const po = allPOs[i];

    if (onProgress) {
      onProgress(i + 1, allPOs.length);
    }

    try {
      const poQty = parseFloat(po.qty) || 0;
      const currentOpenQty = parseFloat(po.openQty) || 0;

      let shipNextToken = null;
      let totalShipped = 0;

      do {
        const { data: shipData } = await client.query({
          query: gql(getShipmentsByPO),
          variables: {
            poID: po.id,
            limit: 1000,
            nextToken: shipNextToken,
          },
          fetchPolicy: "network-only",
        });

        const shipItems = shipData?.receivingAndShippingByPO?.items || [];
        totalShipped += shipItems.reduce(
          (sum, s) => sum + (parseFloat(s.qty) || 0),
          0
        );
        shipNextToken = shipData?.receivingAndShippingByPO?.nextToken;
      } while (shipNextToken);

      const correctOpenQty = poQty - totalShipped;
      const discrepancy = currentOpenQty - correctOpenQty;

      results.push({
        poID: po.id,
        number: po.number || "",
        itemNumber: po.itemNumber || "",
        vendorName: po.vendor?.name || "",
        materialCode: po.materialEntityRelation?.code || "",
        statusCode: po.statusCode || "",
        CRD: po.CRD || "",
        orderedQty: poQty,
        currentOpenQty,
        totalShippedQty: totalShipped,
        correctOpenQty,
        discrepancy,
        hasDiscrepancy: Math.abs(discrepancy) > 0.01,
      });
    } catch (error) {
      console.error(`Error processing PO ${po.number}:`, error);
      results.push({
        poID: po.id,
        number: po.number || "",
        itemNumber: po.itemNumber || "",
        vendorName: po.vendor?.name || "",
        materialCode: po.materialEntityRelation?.code || "",
        statusCode: po.statusCode || "",
        CRD: po.CRD || "",
        orderedQty: parseFloat(po.qty) || 0,
        currentOpenQty: parseFloat(po.openQty) || 0,
        totalShippedQty: null,
        correctOpenQty: null,
        discrepancy: null,
        hasDiscrepancy: null,
        error: error.message,
      });
    }
  }

  return results;
}
